import React, { useEffect, useState } from "react";
import { Box, Typography, TextField } from "@mui/material";
import { useDepthChart } from "../../../../contexts/DepthChartContext";
import JerseyIcon from "../Components/JerseyIcon";
import {
  KickoffPlayLog,
  RosterPlayer,
  TackleLog,
} from "../../../../types/LogTypes";
import { useLogger } from "../../../../contexts/LoggerContext";

export const KickOffTackleLog = () => {
  const [selectedPrimaryTackler, setSelectedPrimaryTackler] =
    useState<RosterPlayer | null>(null);
  const [selectedAssistedTackler, setSelectedAssistedTackler] = useState<
    RosterPlayer[] | []
  >([]);

  const [tackleLog, setTackleLog] = useState<TackleLog | null>(null);

  const [primaryJerseyNumber, setPrimaryJerseyNumber] = useState<string>("");
  const [assistedJerseyNumbers, setAssistedJerseyNumbers] = useState<string[]>(
    []
  );

  const { kickoffPlayers } = useDepthChart();
  const { currentPlayLog, setCurrentPlayLog, clearSelections } = useLogger();

  useEffect(() => {
    if (tackleLog && currentPlayLog) {
      setCurrentPlayLog({
        ...currentPlayLog,
        tackle: tackleLog,
      } as KickoffPlayLog);
    }
  }, [tackleLog]);

  useEffect(() => {
    if (selectedPrimaryTackler) {
      setTackleLog({
        ...tackleLog,
        primary_tackler_roster_player: selectedPrimaryTackler,
      });
    }
  }, [selectedPrimaryTackler]);

  useEffect(() => {
    if (selectedAssistedTackler) {
      setTackleLog({
        ...tackleLog,
        assisted_tacklers_roster_players: selectedAssistedTackler,
      });
    }
  }, [selectedAssistedTackler]);

  useEffect(() => {
    if (clearSelections) {
      setSelectedPrimaryTackler(null);
      setSelectedAssistedTackler([]);
      setPrimaryJerseyNumber("");
      setAssistedJerseyNumbers([]);
    }
  }, [clearSelections]);

  const handlePrimaryJerseyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setPrimaryJerseyNumber(value);
    }
  };

  const handlePrimaryTacklerSelection = (player: RosterPlayer) => {
    setSelectedPrimaryTackler(player);
    setPrimaryJerseyNumber(player.jersey_number?.toString() || "");
    if (selectedPrimaryTackler) {
      setTackleLog({
        ...tackleLog,
        primary_tackler_roster_player: selectedPrimaryTackler,
      });
    }
  };

  const handleSecondaryJerseyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setPrimaryJerseyNumber(value);
    }
  };

  const handleSecondaryTacklerSelection = (player: RosterPlayer) => {
    setSelectedAssistedTackler([...selectedAssistedTackler, player]);
    setAssistedJerseyNumbers([
      ...assistedJerseyNumbers,
      player.jersey_number?.toString() || "",
    ]);
    if (selectedAssistedTackler) {
      setTackleLog({
        ...tackleLog,
        assisted_tacklers_roster_players: selectedAssistedTackler,
      });
    }
  };

  const MAX_JERSEYS_DISPLAYED = 8;

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography>Primary Tackler</Typography>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
          {kickoffPlayers
            .slice(0, MAX_JERSEYS_DISPLAYED)
            .map((player: RosterPlayer) => (
              <JerseyIcon
                key={player.id}
                number={player.jersey_number}
                name={player.player.name}
                onClick={() => handlePrimaryTacklerSelection(player)}
                selected={selectedPrimaryTackler?.id === player.id}
              />
            ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <TextField
            type="number"
            label="Jersey Number"
            value={primaryJerseyNumber}
            fullWidth
            onChange={handlePrimaryJerseyNumberChange}
            inputProps={{
              min: 0,
              max: 99,
              step: 1,
            }}
          />
        </Box>
      </Box>
      <Box>
        <Typography>Assisted Tackler</Typography>
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
          {kickoffPlayers
            .slice(0, MAX_JERSEYS_DISPLAYED)
            .map((player: RosterPlayer) => (
              <JerseyIcon
                key={player.id}
                number={player.jersey_number}
                name={player.player.name}
                onClick={() => handleSecondaryTacklerSelection(player)}
                selected={selectedAssistedTackler.some(
                  (tackler) => tackler.id === player.id
                )}
              />
            ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <TextField
            type="number"
            label="Jersey Number"
            value={assistedJerseyNumbers.join(", ")}
            fullWidth
            onChange={handleSecondaryJerseyNumberChange}
            inputProps={{
              min: 0,
              max: 99,
              step: 1,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
